// /r/{id}: a recording under review (RFC-043 "Recordings", byj-1wv.5.4),
// laid out as a share's page is (byj-1wv.51.4): a bar under the head says
// how its review stands — to its owner, who reviews it and how far they have
// got, with Delete; to a reviewer, the threads still open, with Mark review
// done. Its screen, when it kept one, and its timeline are tabs, Screen
// first, or one over the other; the timeline carries a thread per event and
// per diff line, its Comment shown where the pointer is. Beside them: the
// threads, open first; for the owner, the reviewers with Invite over them;
// what was masked and how long it is kept, folded; and a terminal's
// recording, which is its screen, what was said in it.
import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { lazyChunk } from "../chunk";
import { api, type Annotation, type Recording, type ReviewApi, type User } from "../api";
import type { EventBody } from "../protocol";
import { useConfirm } from "../components/Confirm";
import { useEditRecording } from "../components/EditRecording";
import { countedUses, InviteLinkPopover, type MadeInvite } from "../components/InviteLink";
import { Messages } from "../components/Messages";
import { PageLoading, PageNotice } from "../components/Page";
import { FoldPanel, PeoplePanel } from "../components/SidePanel";
import { StateBar } from "../components/StateBar";
import { daysLeft, instructorLoses } from "../components/RecordingRow";
import { panelId, tabId } from "../components/Tabs";
import { useSplit, ViewBar } from "../components/ViewBar";
import { VSplit } from "../components/VSplit";
import {
  anchorKey,
  castTurns,
  diffAnchor,
  notYetReviewing,
  openThreads,
  recordedEvents,
  threads as toThreads,
  threadsByAnchor,
  upsert,
  type DiffLine,
  type Thread,
} from "../review";
import { navigate } from "../router";
import { t, when, whenDay, whenMinute, whenShort, word, phrases } from "../i18n";
import { buildTimeline, type NoteItem, type SeqEvent } from "../timeline";
import { SplitPane } from "../components/SplitPane";
import { flash, Timeline, type Annotator, type TimelineHandle } from "../components/Timeline";

const PollMs = 15_000;

// the screen's player, loaded only when a recording's screen is opened
const CastPlayer = lazyChunk(() => import("../components/CastPlayer"));

type Loaded = { rec: Recording; events: EventBody[]; annotations: Annotation[] };

/**
 * Where a recording's ← goes, and after its owner deletes it: a class's
 * recording back to its class — its owner to the class page, which lists
 * their recordings, its instructor to the management page's Recordings
 * (byj-1wv.51.6) — anything else to /shares.
 */
export function recordingBack(rec: Pick<Recording, "role" | "class">): { to: string; label: string } {
  if (rec.class && rec.role === "owner") return { to: `/c/${encodeURIComponent(rec.class.id)}`, label: t("recordings:backToClass", { cls: rec.class.name }) };
  if (rec.class && rec.role === "instructor") return { to: `/c/${encodeURIComponent(rec.class.id)}/manage`, label: t("recordings:backToClass", { cls: rec.class.name }) };
  return { to: "/shares", label: t("common:backToShares") };
}

/**
 * The loader: the recording, its events and its annotations, then the view;
 * `thread` is the comment a comment mail's link names (?thread=, byj-1wv.52).
 */
export function Review({ id, me, thread, client = api }: { id: string; me: User; thread?: string | null; client?: ReviewApi }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      const rec = await client.recording(id);
      if (!rec.uploaded) return { rec, events: [], annotations: [] };
      const [events, annotations] = await Promise.all([client.recordingEvents(id), client.annotations(id)]);
      return { rec, events, annotations };
    })().then(
      (l) => live && setLoaded(l),
      (err: unknown) => live && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      live = false;
    };
  }, [id, client]);
  if (error) {
    return (
      <PageNotice error={error}>
        <a href="/shares" onClick={(e) => (e.preventDefault(), navigate("/shares"))}>
          {t("common:backToShares")}
        </a>
      </PageNotice>
    );
  }
  if (!loaded) return <PageLoading />;
  if (!loaded.rec.uploaded) return <PageNotice message={t("recordings:reviewNotUploaded")} />;
  return <ReviewView {...loaded} me={me} client={client} openThread={thread} />;
}

export function ReviewView({
  rec: initial,
  events,
  annotations: initialAnnotations,
  me,
  client,
  openThread,
}: Loaded & { me: User; client: ReviewApi; openThread?: string | null }) {
  const [rec, setRec] = useState(initial);
  const [confirm, confirmDialog] = useConfirm();
  const [annotations, setAnnotations] = useState(initialAnnotations);
  const [error, setError] = useState<string | null>(null);
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : String(err));
  const back = recordingBack(rec);
  // a terminal share's recording is its screen (RFC-043 model 21,
  // byj-1wv.22): the cast alone, no timeline, what was said beside it
  const terminal = initial.source === "term";
  // its screen (byj-1wv.5.7), when it kept one: the page opens on it, as a
  // share's does, and each turn of the timeline plays it from there
  const [tab, setTab] = useState<"timeline" | "screen">(initial.screen ? "screen" : "timeline");
  const split = useSplit("share.review.split", rec.screen === true && !terminal);
  const splitOn = split.splitOn;
  const [cast, setCast] = useState<string | null>(null);
  // why the screen did not load, said where it would have been
  const [castError, setCastError] = useState<string | null>(null);
  const [seek, setSeek] = useState<{ turn: number; nonce: number } | undefined>(undefined);
  // a terminal's recording is its screen, flagged or not: asked for always,
  // so one whose screen is missing says so rather than loading for ever
  const wantsScreen = initial.screen === true || terminal;
  useEffect(() => {
    if (!wantsScreen) return;
    let live = true;
    client.recordingScreen(initial.id).then(
      (c) => live && setCast(c),
      (err: unknown) => live && setCastError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      live = false;
    };
  }, [client, initial.id, wantsScreen]);
  // the turns the screen saw begin: ▶ screen on those alone, none until it
  // has loaded — a turn with no marker has no place on it to go to
  const playable = useMemo(() => (cast === null ? new Set<number>() : castTurns(cast)), [cast]);
  // a turn's ▶ screen: the player seeks to it, shown first where it is a tab
  const play = useCallback(
    (turn: number) => {
      if (!splitOn) setTab("screen");
      setSeek((s) => ({ turn, nonce: (s?.nonce ?? 0) + 1 }));
    },
    [splitOn],
  );

  const timeline = useRef<TimelineHandle | null>(null);
  // the places whose threads are shown on the timeline, by anchorKey: each
  // folded to a count until it is opened, from its badge or from the side
  const [shown, setShown] = useState<ReadonlySet<string>>(() => new Set());
  const show = useCallback((key: string, on: boolean) => {
    setShown((s) => {
      if (s.has(key) === on) return s;
      const next = new Set(s);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const shownThreads = useMemo(() => ({ shown, show }), [shown, show]);
  // a thread picked from the side: shown where it is, and the timeline — up
  // first, when the screen is the tab — scrolled to it, the event and the
  // thread lit together whichever kind of event it is on; a reply named,
  // as a comment mail names it, lit in its thread too
  const [jumpTo, setJumpTo] = useState<{ th: Thread; note?: string } | null>(null);
  const jump = (th: Thread, note?: string) => {
    show(anchorKey(th.root.seq_ref, th.root.anchor), true);
    if (!splitOn && tab !== "timeline") setTab("timeline");
    setJumpTo({ th, note });
  };
  useEffect(() => {
    if (jumpTo && timeline.current) {
      const { th, note } = jumpTo;
      timeline.current.scrollToSeqs([th.root.seq_ref]);
      // the relay's ids are letters, digits and _: nothing to escape
      const card = document.querySelector(`[data-thread="${th.root.id}"]`);
      if (card) {
        // a resolved one, folded to its line, opens to what it said
        if (card instanceof HTMLDetailsElement) card.open = true;
        flash(card);
        const reply = note && note !== th.root.id ? card.querySelector(`[data-annotation="${note}"]`) : null;
        if (reply) flash(reply);
        // the event is centred; what was asked for may be further down it,
        // under the other threads there: the thread brought into view, and
        // a reply to the middle, not left at an edge
        if (reply) reply.scrollIntoView({ block: "center" });
        else card.scrollIntoView({ block: "nearest" });
      }
      setJumpTo(null);
    }
  }, [jumpTo, tab]);

  const seqEvents = useMemo(() => recordedEvents(events), [events]);
  const turns = useMemo(() => buildTimeline(seqEvents), [seqEvents]);
  const all = useMemo(() => toThreads(annotations), [annotations]);
  const at = useMemo(() => threadsByAnchor(all), [all]);
  // how many threads are on each event, its diff's lines' included: a
  // tool with some on its result opens
  const bySeq = useMemo(() => {
    const m = new Map<number, number>();
    for (const th of all) m.set(th.root.seq_ref, (m.get(th.root.seq_ref) ?? 0) + 1);
    return m;
  }, [all]);
  const open = useMemo(() => openThreads(all), [all]);
  // a comment mail's link names a comment, ?thread=<id> (byj-1wv.52): the
  // page opens on its thread — shown at its place, the timeline up, both
  // lit, and a reply in it too — as a card in the side does; one it does
  // not know opens the page as it would. The id is then taken off the
  // address, so a reload or a way back does not go there again.
  useEffect(() => {
    if (!openThread) return;
    const th = all.find((x) => x.root.id === openThread || x.replies.some((a) => a.id === openThread));
    if (th) jump(th, openThread);
    const rest = new URLSearchParams(window.location.search);
    rest.delete("thread");
    const q = rest.toString();
    navigate(window.location.pathname + (q ? `?${q}` : "") + window.location.hash, true);
  }, []); // once, as the page opens
  const names = useMemo(() => {
    const m = new Map<string, string>([[rec.owner.id, rec.owner.name]]);
    for (const a of annotations) m.set(a.author.id, a.author.name);
    m.set(me.id, me.name || me.email);
    return m;
  }, [rec.owner, annotations, me]);
  // what was said in a terminal's recording, as its share's chat showed it
  const said: NoteItem[] = useMemo(
    () =>
      events.flatMap((e, i) => (e.kind === "note" && e.text ? [{ key: `n${i}`, author: e.by, text: e.text, ts: "", seq: i }] : [])),
    [events],
  );

  // Others' annotations arrive by polling: a review is not a live session.
  useEffect(() => {
    const timer = setInterval(() => {
      client.annotations(rec.id).then(setAnnotations, () => undefined);
    }, PollMs);
    return () => clearInterval(timer);
  }, [client, rec.id]);

  const post = useCallback(
    async (a: { seq_ref: number; anchor: string; body: string; parent_id?: string }) => {
      setError(null);
      try {
        const saved = await client.annotate(rec.id, a);
        setAnnotations((as) => upsert(as, saved));
        return true;
      } catch (err) {
        fail(err);
        return false;
      }
    },
    [client, rec.id],
  );
  const resolve = useCallback(
    async (a: Annotation, resolved: boolean) => {
      setError(null);
      try {
        const saved = await client.setResolved(rec.id, a.id, resolved);
        setAnnotations((as) => upsert(as, saved));
      } catch (err) {
        fail(err);
      }
    },
    [client, rec.id],
  );

  // who a comment is mailed to (byj-1wv.52), said under the form that
  // writes it: a new thread to the owner, a reply to everyone else who has
  // written in its thread; to nobody, nothing said
  // — each named as the mail names them, by their part with no name
  const mailName = useCallback(
    (u: { id: string; name: string }) => u.name.trim() || (u.id === rec.owner.id ? t("recordings:mailedOwner") : t("recordings:mailedReviewer")),
    [rec.owner.id],
  );
  const mailNew = me.id === rec.owner.id ? undefined : phrases.mailedTo([mailName(rec.owner)]);
  const mailReply = useCallback(
    (th: Thread) => {
      const who = new Map<string, string>();
      for (const a of [th.root, ...th.replies]) if (a.author.id !== me.id) who.set(a.author.id, mailName(a.author));
      return who.size > 0 ? phrases.mailedTo([...who.values()]) : undefined;
    },
    [me.id, mailName],
  );

  const annotator: Annotator = useMemo(() => {
    // the threads at one place, folded to a badge until shown; a thread
    // started there shows them. `also`'s threads (a permission's request,
    // once answered) are drawn with them, a new one going on `ev`
    const anchor = (ev: SeqEvent, where: string, label: string, compact: boolean, also?: SeqEvent) => {
      const key = anchorKey(ev.seq, where);
      const keys = also ? [key, anchorKey(also.seq, where)] : [key];
      return (
        <Anchor
          threads={keys.flatMap((k) => at.get(k) ?? [])}
          label={label}
          compact={compact}
          anchorKeys={keys}
          onPost={async (body) => {
            const ok = await post({ seq_ref: ev.seq, anchor: where, body });
            if (ok) show(key, true);
            return ok;
          }}
          // a reply goes where its thread is, which may be `also`
          onReply={(root, body) => post({ seq_ref: root.seq_ref, anchor: root.anchor, body, parent_id: root.id })}
          onResolve={resolve}
          mailNew={mailNew}
          mailReply={mailReply}
        />
      );
    };
    return {
      event: (ev: SeqEvent, also?: SeqEvent) => anchor(ev, "event", t("recordings:comment"), false, also),
      line: (ev: SeqEvent, l: DiffLine) => anchor(ev, diffAnchor(l.file, l.line), t("recordings:commentOnLine", { file: l.file, line: l.line }), true),
      count: (ev: SeqEvent) => bySeq.get(ev.seq) ?? 0,
    };
  }, [at, bySeq, show, post, resolve, mailNew, mailReply]);

  const setHandle = useCallback((h: TimelineHandle) => {
    timeline.current = h;
  }, []);

  // its name and how long it is kept are its owner's to change (byj-1wv.38,
  // byj-1wv.43)
  const [editRecording, editDialog] = useEditRecording();
  const rename = async () => {
    const patch = await editRecording(rec);
    if (patch === null) return;
    try {
      const updated = await client.updateRecording(rec.id, patch);
      setRec((r) => ({ ...r, title: updated.title, expires_at: updated.expires_at, retention_days: updated.retention_days }));
    } catch (err) {
      fail(err);
    }
  };

  // the owner's alone: the recording, its screen and its reviewer links go,
  // and the page goes back to the list (byj-1wv.33)
  const del = async () => {
    // a class's instructor loses one made under an assignment: said first
    if (!(await confirm(phrases.deleteRecordingConfirm(rec.title || rec.id, instructorLoses(rec)), { action: t("recordings:deleteRecording"), danger: true }))) return;
    try {
      await client.deleteRecording(rec.id);
      navigate(back.to);
    } catch (err) {
      fail(err);
    }
  };

  const done = async () => {
    if (!(await confirm(t("recordings:reviewDoneConfirm"), { action: t("recordings:reviewDone") }))) return;
    try {
      await client.reviewDone(rec.id);
      setRec((r) => ({ ...r, done_at: new Date().toISOString() }));
    } catch (err) {
      fail(err);
    }
  };

  // the reviewer links made on this page, newest first: the relay cannot show them again
  const [invites, setInvites] = useState<MadeInvite[]>([]);
  const owner = rec.role === "owner";

  const timelineView = (
    <ShownThreads.Provider value={shownThreads}>
      <Timeline
        turns={turns}
        names={names}
        handle={setHandle}
        annotator={annotator}
        startAtEnd={false}
        firstOpen
        onPlay={rec.screen ? play : undefined}
        playable={playable}
      />
    </ShownThreads.Provider>
  );
  const screenView =
    castError !== null ? (
      <p className="error screen-error">{t("recordings:screenNotLoaded", { why: castError })}</p>
    ) : cast === null ? (
      <p className="muted">{t("session:screenLoading")}</p>
    ) : (
      <div className="review-screen">
        <p className="muted small">{t("session:screenMaskedNote")}</p>
        <Suspense fallback={<p className="muted">{t("session:screenLoading")}</p>}>
          <CastPlayer cast={cast} seek={seek} onSeeked={() => setSeek(undefined)} />
        </Suspense>
      </div>
    );

  return (
    <div className="session review">
      {confirmDialog}
      {editDialog}
      <header className="session-head">
        <a
          className="back"
          href={back.to}
          onClick={(e) => {
            e.preventDefault();
            navigate(back.to);
          }}
          aria-label={back.label}
          title={back.label}
        >
          ←
        </a>
        {/* its name, and beside it the owner's Edit */}
        <h1>{rec.title || rec.id}</h1>
        {owner && (
          <button className="small" aria-label={t("recordings:editRecordingHeading")} onClick={() => void rename()}>
            {t("common:edit")}
          </button>
        )}
        <span className="grow" />
        <span className="muted small">
          {phrases.source(rec.source)} · {phrases.recordingTurns(rec.turns[0], rec.turns[1])}
        </span>
        <span className={`badge badge-${rec.role}`}>
          <span className="badge-role">{word("common:role", rec.role)}</span>
        </span>
      </header>
      <RecordingBar rec={rec} open={terminal ? undefined : open.length} onDone={() => void done()} onDelete={() => void del()} />
      {error && <p className="banner error">{t("common:error", { m: error })}</p>}

      {!terminal && rec.screen && (
        <ViewBar prefix="review" label={t("session:sessionTabs")} view={tab} onView={setTab} screenLabel={t("session:tabScreenRecorded")} split={split} />
      )}

      <SplitPane
        storageKey="share.review.side"
        sideTitle={t("session:sidePanel")}
        main={
          <div
            className="session-main"
            {...(!terminal && rec.screen && !splitOn ? { role: "tabpanel", id: panelId("review", tab), "aria-labelledby": tabId("review", tab) } : {})}
          >
            {terminal ? (
              screenView
            ) : (
              // one split for every way of seeing it — the screen over the
              // timeline, or either alone — so the timeline is never drawn
              // again: its folds, where it was scrolled to and a comment half
              // written outlive a tab and the split turned on and off
              <VSplit
                storageKey="share.review.split.top"
                label={t("session:splitResize")}
                topLabel={t("session:tabScreenRecorded")}
                bottomLabel={t("session:tabTimeline")}
                only={splitOn ? undefined : tab === "screen" && rec.screen ? "top" : "bottom"}
                top={screenView}
                bottom={timelineView}
              />
            )}
          </div>
        }
        side={
          <aside className="side session-side review-side">
            {/* top to bottom, as a share's side is: the recording folded, the
                reviewers, and last, filling what height is left, the threads
                (a terminal's chat), as a share's notes are */}
            <RecordingPanel rec={rec} />
            {owner && (
              <PeoplePanel
                label={t("recordings:reviewers")}
                count={(rec.reviewers ?? []).length + notYetReviewing(rec).length}
                invite={
                  <InviteLinkPopover
                    id={rec.id}
                    uses={countedUses}
                    initial={{ role: "reviewer", uses: 1, ttl: phrases.ttlOptions()[2]![0] }}
                    create={(c) => client.createRecordingInvite(rec.id, c.uses, c.ttl)}
                    made={invites}
                    onMade={(inv) => setInvites((xs) => [inv, ...xs])}
                  />
                }
              >
                {(rec.reviewers ?? []).length + notYetReviewing(rec).length === 0 ? (
                  <p className="muted">{t("recordings:noReviewers")}</p>
                ) : (
                  <ul className="people">
                    {(rec.reviewers ?? []).map((r) => (
                      <li key={r.id} className="row">
                        <span className="grow">{r.name}</span>
                        <span className={`chip ${r.done_at ? "chip-ok" : ""}`} title={r.done_at ? when(r.done_at) : undefined}>
                          {r.done_at ? t("recordings:reviewerDone", { when: whenShort(r.done_at) }) : t("recordings:reviewerReviewing")}
                        </span>
                      </li>
                    ))}
                    {/* asked by email and not come yet (byj-xgt): by their address */}
                    {notYetReviewing(rec).map((q) => (
                      <li key={q.email} className="row" data-testid="asked">
                        <span className="grow">{q.email}</span>
                        <span className={`chip ${q.state === "waiting" ? "" : "chip-warn"}`} title={t("shares:askedOn", { when: when(q.requested_at) })}>
                          {word("shares:requestState", q.state)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </PeoplePanel>
            )}
            {terminal ? (
              <section className="panel side-fill" aria-label={t("session:recordedChat")} data-testid="recorded-chat">
                <h3>{t("session:recordedChat")}</h3>
                <div className="side-fill-scroll">
                  {said.length === 0 ? <p className="muted">{t("session:recordedNoChat")}</p> : <Messages notes={said} names={names} me={me.id} />}
                </div>
              </section>
            ) : (
              <ThreadsPanel all={all} open={open} events={events} onJump={jump} />
            )}
          </aside>
        }
      />
    </div>
  );
}

/**
 * The bar under the head, in view whether the side panel is open, folded or
 * under the main pane: to the owner, how long the recording is kept and how
 * far its reviewers have got, with Delete (asked first); to a reviewer, whose
 * it is and the threads still open, with Mark review done (asked first) until
 * it is, and when it was after.
 */
function RecordingBar({ rec, open, onDone, onDelete }: {
  rec: Recording;
  /** the open threads; none for a terminal's recording, which takes none */
  open?: number;
  onDone: () => void;
  onDelete: () => void;
}) {
  const owner = rec.role === "owner";
  // an instructor reads a student's recording, and marks nothing done
  const state = owner ? "owner" : rec.role === "instructor" ? "instructor" : rec.done_at ? "done" : "reviewing";
  const reviewers = rec.reviewers ?? [];
  // those asked by email and not come yet count among them, as on /shares' row (byj-xgt)
  const all = reviewers.length + notYetReviewing(rec).length;
  const facts: string[] = owner
    ? [t("recordings:keptUntilShort", { when: whenShort(rec.expires_at) }), phrases.reviewersDone(reviewers.filter((r) => r.done_at).length, all)]
    : [t("recordings:recordingOf", { name: rec.owner.name }), ...(rec.requested_at ? [t("shares:askedOn", { when: whenDay(rec.requested_at) })] : [])];
  // a class's: who besides its reviewers reads it, so its owner is not told no one does
  if (owner && rec.class) facts.push(rec.assignment ? t("recordings:instructorCanOpen", { cls: rec.class.name }) : t("recordings:instructorCannotOpen", { cls: rec.class.name }));
  if (open !== undefined) facts.push(phrases.openThreadCount(open));
  return (
    <StateBar
      className="recording-bar"
      label={t("recordings:recordingBar")}
      state={state}
      stateLabel={
        rec.done_at && !owner ? (
          // done: a mark of its own, with the day, the full time on hover
          <span className="share-bar-state review-done-mark">
            <span className="review-done-check" aria-hidden="true">
              ✓
            </span>
            {t("recordings:recordingBarState.done")}
            <time dateTime={rec.done_at} title={when(rec.done_at)}>
              {whenShort(rec.done_at)}
            </time>
          </span>
        ) : (
          <span className="share-bar-state">{word("recordings:recordingBarState", state)}</span>
        )
      }
      actions={
        owner ? (
          <button className="small danger" onClick={onDelete}>
            {t("recordings:deleteRecordingLong")}
          </button>
        ) : (
          // an instructor reads a student's recording; only a reviewer marks it done
          rec.role === "reviewer" &&
          !rec.done_at && (
            <button className="small primary" onClick={onDone}>
              {t("recordings:reviewDone")}
            </button>
          )
        )
      }
    >
      <span className="share-bar-facts">{facts.join(" · ")}</span>
    </StateBar>
  );
}

/**
 * The threads, open first, the resolved folded under them: each a card with
 * where it is and every comment in it — who, when, what — that goes there,
 * showing it on the timeline.
 */
function ThreadsPanel({ all, open, events, onJump }: {
  all: Thread[];
  open: Thread[];
  events: EventBody[];
  onJump: (th: Thread) => void;
}) {
  const resolved = all.filter((th) => th.root.resolved);
  const item = (th: Thread) => (
    <li key={th.root.id}>
      <button className="thread-link" onClick={() => onJump(th)} title={t("recordings:goToThread")}>
        <span className="thread-link-at">
          {t("recordings:threadAt", { turn: events[th.root.seq_ref]?.turn ?? 0, what: th.root.anchor === "event" ? t("recordings:anchorEvent") : th.root.anchor.slice("diff:".length) })}
          <span aria-hidden="true">→</span>
        </span>
        {[th.root, ...th.replies].map((a) => (
          <span key={a.id} className={`thread-link-comment${a === th.root ? "" : " reply"}`}>
            <span className="thread-link-who">
              <strong>{a.author.name}</strong>
              <time dateTime={a.created_at} title={when(a.created_at)}>
                {whenShort(a.created_at)}
              </time>
            </span>
            <span className="thread-link-body">{a.body}</span>
          </span>
        ))}
      </button>
    </li>
  );
  return (
    <section className="panel side-fill" aria-label={t("recordings:threadsTitle")}>
      <h3>
        {t("recordings:threadsTitle")} <span className="tab-count">{open.length}</span>
      </h3>
      {/* the head stays; the cards scroll under it */}
      <div className="side-fill-scroll">
        {open.length === 0 ? (
          <p className="muted">{t("recordings:noOpenThreads")}</p>
        ) : (
          <ul className="threads" data-testid="open-threads">
            {open.map(item)}
          </ul>
        )}
        {resolved.length > 0 && (
          <details className="threads-resolved">
            <summary>{t("recordings:resolvedThreads", { n: resolved.length })}</summary>
            <ul className="threads">{resolved.map(item)}</ul>
          </details>
        )}
      </div>
    </section>
  );
}

/**
 * The recording: folded to what was masked before upload, a chip a kind,
 * and the day it goes; open, a line a fact, each under its name — what was
 * masked, when it was recorded and until when it is kept (the days left),
 * what it is of, whether its screen was kept.
 */
function RecordingPanel({ rec }: { rec: Recording }) {
  const f = t("recordings:recordingFacts", { returnObjects: true });
  const left = daysLeft(rec);
  return (
    <FoldPanel
      label={t("recordings:recordingPanel")}
      summary={
        <>
          {rec.masked.length === 0 ? (
            <span className="chip">{t("recordings:maskedNone")}</span>
          ) : (
            rec.masked.map((m) => (
              <span key={m.kind} className="chip chip-masked" title={t("recordings:maskedSummary")}>
                {t("session:masked", { kind: m.kind, count: m.count })}
              </span>
            ))
          )}
          <span className="chip">{t("recordings:keptUntilShort", { when: whenShort(rec.expires_at) })}</span>
        </>
      }
    >
      <dl className="status recording-facts">
        <dt>{f.masked}</dt>
        <dd>
          {rec.masked.length === 0
            ? t("recordings:maskedNothing")
            : rec.masked.map((m) => (
                <span key={m.kind} className="chip chip-masked">
                  {t("session:masked", { kind: m.kind, count: m.count })}
                </span>
              ))}
        </dd>
        <dt>{f.recorded}</dt>
        <dd>{whenMinute(rec.created_at)}</dd>
        <dt>{f.kept}</dt>
        <dd>
          {whenMinute(rec.expires_at)}
          {left !== null && <span className="muted"> · {phrases.daysLeft(left)}</span>}
        </dd>
        <dt>{f.source}</dt>
        <dd>
          {phrases.source(rec.source)} · {phrases.recordingTurns(rec.turns[0], rec.turns[1])}
        </dd>
        <dt>{f.screen}</dt>
        <dd>{rec.screen ? t("recordings:screenWasKept") : t("recordings:screenWasNotKept")}</dd>
        {/* a reviewer's: the note they were asked with (byj-xgt) */}
        {rec.request_note && (
          <>
            <dt>{t("recordings:requestNoteFact")}</dt>
            <dd className="request-note">{rec.request_note}</dd>
          </>
        )}
      </dl>
    </FoldPanel>
  );
}

/**
 * Which places' threads are shown on the timeline, and how one is shown or
 * hidden: read by each Anchor itself, so a badge opened or closed draws its
 * anchor again and not the whole timeline it is in — the annotator, and the
 * memoized Timeline, stay as they were.
 */
const ShownThreads = createContext<{ shown: ReadonlySet<string>; show: (key: string, on: boolean) => void }>({
  shown: new Set(),
  show: () => {},
});

/**
 * The threads at one anchor, and a way to start another, at the right of
 * what they are about: how many threads there are, a badge that shows or
 * hides them, and Comment, shown where the pointer or the focus is (always,
 * on a touch screen); on a diff line, the badge and a "+" at the line's end.
 */
function Anchor({
  threads,
  label,
  compact = false,
  anchorKeys,
  onPost,
  onReply,
  onResolve,
  mailNew,
  mailReply,
}: {
  threads: Thread[];
  label: string;
  compact?: boolean;
  /** which places it is (one, or an item's two): whether their threads are shown is read from ShownThreads */
  anchorKeys: string[];
  onPost: (body: string) => Promise<boolean>;
  onReply: (root: Annotation, body: string) => Promise<boolean>;
  onResolve: (root: Annotation, resolved: boolean) => void;
  /** who a new thread here is mailed to, and a reply in a thread */
  mailNew?: string;
  mailReply: (th: Thread) => string | undefined;
}) {
  const [writing, setWriting] = useState(false);
  const { shown: all, show } = useContext(ShownThreads);
  const shown = anchorKeys.some((k) => all.has(k));
  const onShow = (on: boolean) => anchorKeys.forEach((k) => show(k, on));
  const open = threads.filter((th) => !th.root.resolved).length;
  const badge = threads.length > 0 && (
    <button
      className={`small thread-badge${open === 0 ? " all-resolved" : ""}`}
      aria-expanded={shown}
      aria-label={shown ? t("recordings:hideThreads", { count: threads.length }) : t("recordings:showThreads", { count: threads.length })}
      onClick={() => onShow(!shown)}
    >
      {open === 0 ? "✓" : "💬"} {threads.length}
    </button>
  );
  const list = shown
    ? threads.map((th) => <ThreadView key={th.root.id} thread={th} onReply={onReply} onResolve={onResolve} mailed={mailReply(th)} />)
    : [];
  const start = writing ? (
    <CommentForm
      title={t("recordings:newThread")}
      placeholder={t("recordings:commentPlaceholder")}
      submit={t("recordings:post")}
      mailed={mailNew}
      onCancel={() => setWriting(false)}
      onSubmit={async (body) => {
        const ok = await onPost(body);
        if (ok) setWriting(false);
        return ok;
      }}
    />
  ) : (
    <button
      className={`small comment-button ${compact ? "comment-line" : ""}`}
      onClick={() => setWriting(true)}
      aria-label={label}
      title={label}
    >
      {compact ? (
        t("recordings:commentLineMark")
      ) : (
        <>
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
          {/* the icon alone on a touch screen, where it is always there */}
          <span className="comment-text">{t("recordings:comment")}</span>
        </>
      )}
    </button>
  );
  // On a diff line the badge and "+" stay at the line's end and its threads
  // wrap below; on an event they stand at its right, its threads under it.
  // A new thread is written right under what it is about, above the threads
  // already there, wherever they are shown or not: where it is written is
  // where it is anchored, never in a reply's place under another thread.
  if (compact) {
    return (
      <div className="anchor anchor-line">
        {badge}
        {start}
        {list}
      </div>
    );
  }
  return (
    <div className="anchor">
      <span className="anchor-tools">
        {badge}
        {!writing && start}
      </span>
      {writing && start}
      {list}
    </div>
  );
}

/**
 * A thread as a card: each comment under who wrote it and when — the time
 * short, the full one on hover — the replies a step in; Reply and Resolve at
 * its foot. A resolved thread is folded to one line.
 */
function ThreadView({
  thread,
  onReply,
  onResolve,
  mailed,
}: {
  thread: Thread;
  onReply: (root: Annotation, body: string) => Promise<boolean>;
  onResolve: (root: Annotation, resolved: boolean) => void;
  /** who a reply here is mailed to */
  mailed?: string;
}) {
  const { root, replies } = thread;
  const [replying, setReplying] = useState(false);
  const body = (
    <>
      {[root, ...replies].map((a) => (
        <div key={a.id} className={`annotation${a === root ? "" : " annotation-reply"}`} data-annotation={a.id}>
          <div className="annotation-head">
            <span className="annotation-author">{a.author.name}</span>
            <time dateTime={a.created_at} title={when(a.created_at)}>
              {whenShort(a.created_at)}
            </time>
          </div>
          <p className="annotation-body">{a.body}</p>
        </div>
      ))}
      {replying ? (
        <CommentForm
          placeholder={t("recordings:replyPlaceholder")}
          submit={t("recordings:reply")}
          mailed={mailed}
          onCancel={() => setReplying(false)}
          onSubmit={async (text) => {
            const ok = await onReply(root, text);
            if (ok) setReplying(false);
            return ok;
          }}
        />
      ) : (
        <div className="thread-actions">
          <button className="small" onClick={() => setReplying(true)}>
            {t("recordings:reply")}
          </button>
          <button className="small" onClick={() => onResolve(root, !root.resolved)}>
            {root.resolved ? t("recordings:reopen") : t("recordings:resolve")}
          </button>
        </div>
      )}
    </>
  );
  if (root.resolved) {
    return (
      <details className="thread thread-resolved" data-testid="thread" data-thread={root.id}>
        <summary>
          <span className="thread-resolved-mark">✓ {t("recordings:resolved")}</span> · {root.author.name}: {root.body}
          {replies.length > 0 && ` · ${t("recordings:replies", { count: replies.length })}`}
        </summary>
        {body}
      </details>
    );
  }
  return (
    <div className="thread" data-testid="thread" data-thread={root.id}>
      {body}
    </div>
  );
}

/** A comment to write: a new thread's, a card under its title, or a reply's, in its thread. */
function CommentForm({
  title,
  placeholder,
  submit,
  mailed,
  onSubmit,
  onCancel,
}: {
  /** a new thread's: said over it, so it is not taken for a reply */
  title?: string;
  placeholder: string;
  submit: string;
  /** who it is mailed to, said under it */
  mailed?: string;
  onSubmit: (body: string) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    const ok = await onSubmit(text.trim());
    setBusy(false);
    if (ok) setText("");
  };
  return (
    <form className={`comment-form${title ? " new-thread" : ""}`} aria-label={title} onSubmit={(e) => void go(e)}>
      {title && <span className="new-thread-title">{title}</span>}
      <textarea
        value={text}
        placeholder={placeholder}
        aria-label={placeholder}
        rows={2}
        onChange={(e) => setText(e.target.value)}
        autoFocus
      />
      <div className="row">
        <button type="submit" className="primary" disabled={busy || !text.trim()}>
          {submit}
        </button>
        <button type="button" className="small" onClick={onCancel}>
          {t("common:cancel")}
        </button>
        {mailed && <span className="comment-form-mailed muted small">{mailed}</span>}
      </div>
    </form>
  );
}
